import { html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { StoreState } from '../../../store/store.interface';
import { getSelectedCountryState, resetStateResponse } from '../../country-selector/country-state';
import { resetSchoolResponse } from '../../user-profile';
import { getRepresentativesListUrl, getRepresentativeSchoolListUrl } from '../utils/representative.utils';
import emit from '../../../common/events';

enum SearchType {
  SCHOOL = 'school',
  ZIPCODE = 'zipcode'
}

/**
 * Component to search for Representatives by state and school or by zip code.
 *
 * The state is used to filter schools. The school selection takes the user to a list of representatives for that school.
 * If a zip code is provided, it takes the user to a list of schools in that area.
 */
@customElement('mhecomm-representative-search')
export class RepresentativeSearch extends connect(store)(BaseComponent) {

  @query('mhecomm-school-selector')
  protected schoolSelector: HTMLFormElement | undefined;

  @query('mhecomm-country-state-selector')
  protected stateSelector: HTMLFormElement | undefined;

  @state()
  private selectedState: any;

  @state()
  private selectedSchool: any;

  @state()
  private zipcode: string = '';

  @state()
  private searchType: SearchType = SearchType.SCHOOL;

  private readonly type: string = `ltr-search`;

  stateChanged(state: StoreState) {
    this.selectedState = getSelectedCountryState(this.type, state);
  }

  connectedCallback() {
    super.connectedCallback();
    this.stateSelector?.resetSelectOption();
    store.dispatch(resetStateResponse())
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('selectedState')) {
      this.schoolSelector?.resetSelectOption();
      store.dispatch(resetSchoolResponse());
    }
  }

  renderComp() {
    return html`
      <div class="row">
        <div class="fw-medium mb-1">Search by:</div>
        <div class="row mb-2">
          <ul class="col-12 d-flex mt-1">
            <li class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="search-type-options"
                id="school-option"
                .checked=${this.searchType === SearchType.SCHOOL}
                @change=${() => this.searchType = SearchType.SCHOOL}>
              <label class="form-check-label" for="school-option">
                State & School
              </label>
            </li>
            <li class="form-check ms-4">
              <input
                class="form-check-input"
                type="radio"
                name="search-type-options"
                id="zipcode-option"
                .checked=${this.searchType === SearchType.ZIPCODE}
                @change=${() => this.searchType = SearchType.ZIPCODE}>
              <label class="form-check-label" for="zipcode-option">
                Zip Code
              </label>
            </li>
          </ul>
        </div>
        ${this.searchType === SearchType.SCHOOL ? this.renderSchoolSearch() : this.renderZipcodeSearch()}
      </div>
      <button
        class="btn btn-primary mt-4"
        @click=${() => this.onClick()}
        ?disabled=${this.shouldDisableSearch()}>
        Search
      </button>
    `;
  }

  private shouldDisableSearch() {
    if (this.searchType === SearchType.SCHOOL) {
      return !this.selectedSchool;
    }
    return this.zipcode.trim() === '' || !this.isZipcodeValid();
  }

  private renderSchoolSearch() {
    return html`
      <div class="row">
          <div class="col-6">
            <mhecomm-country-state-selector
              id="state-selector"
              type="${this.type}"
              label="State:"
              countryCode="US"
              selectedOption=${this.selectedState}
              errorMessage="Please select a valid state"
              ?capitalize=${true}
              ?isdirty=${false}
            >
            </mhecomm-country-state-selector>
          </div>
          <div class="col-6">
            <mhecomm-school-selector
              id="school-selector"
              countryCode="US"
              segment="HE"
              label="School:"
              .stateCode=${this.selectedState}
              errorMessage="Please select a valid school"
              requireTextInput=${false}
              ?isdirty=${false}
              ?disabled=${!this.selectedState}
              @onSchoolSelection=${(event) => this.onSchoolSelection(event.detail)}
            >
            </mhecomm-school-selector>
          </div>
        </div>
    `;
  }

  private renderZipcodeSearch() {
    return html`
      <div class="row">
        <div class="col-6">
          <label for="zipcode" class="form-label ${!this.isZipcodeValid() ? 'is-invalid' : ''}">Zip Code:</label>
          <input
            type="text" class="form-control" id="zipcode"
            @input=${(_e: InputEvent) => this.zipcode = (_e.target as HTMLInputElement).value}
          />
          ${!this.isZipcodeValid() ? html`
            <div class="mt-1 invalid-feedback d-block" aria-live="assertive">
              <uxt-icon icon="exclamation-triangle"></uxt-icon>
              <span class="visually-hidden">Error&nbsp;</span>&nbsp;Zip Code must contain exactly 5 numbers.
            </div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private isZipcodeValid(): boolean {
    return this.zipcode.trim() === '' || /^\d{5}$/.test(this.zipcode);
  }

  private onSchoolSelection(school) {
    this.selectedSchool = school;
  }

  private onClick() {
    this.emitSearchClickEvent();
    if (this.selectedSchool) {
      this.goToLtrListUrl();
    } else if (this.zipcode.trim() !== '') {
      this.goToLtrSchoolListUrl();
    }
  }

  private emitSearchClickEvent() {
    emit({
      element: this,
      name: 'ltr-search-click',
      detail: {
        eventName: 'ltr-search-click',
        searchType: this.selectedSchool ? 'school' : 'zipcode',
        searchFilters: {
          schoolName: this.selectedSchool?.name,
          schoolId: this.selectedSchool?.xid,
          state: this.selectedSchool ? this.selectedState : undefined,
          zipcode: this.zipcode ?? undefined,
        }
      }
    });
  }

  private goToLtrListUrl() {
    window.location.href = getRepresentativesListUrl(this.selectedSchool.xid);
  }

  private goToLtrSchoolListUrl() {
    window.location.href = getRepresentativeSchoolListUrl(this.zipcode);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-representative-search': RepresentativeSearch;
  }
}
